import React from 'react';
import { Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import { useIntl } from 'react-intl';
import { SelectOption } from '../../../../../Components/Form/Form.types';
import { outlinedRedBtn } from '../OtherRequirementWizard/StepButtonBar';

const PRIMARY = '#c4122f';
const GREEN = '#4caf50';

interface Step5RequirementNewCompanyUnitPreviewProps {
  selectedRequirement?: { id: number; name: string; number: string };
  selectedCompanyUnits: SelectOption[];
  selectedManagers: SelectOption[];
  onSaveAndQuit: () => Promise<void>;
  onSaveAndNewConnection: () => Promise<void>;
  isSaving?: boolean;
}

const Step5RequirementNewCompanyUnitPreview = ({
  selectedRequirement,
  selectedCompanyUnits,
  selectedManagers,
  onSaveAndQuit,
  onSaveAndNewConnection,
  isSaving = false,
}: Step5RequirementNewCompanyUnitPreviewProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const paperPadding = isMobile ? theme.spacing(2) : theme.spacing(3);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ width: '100%', maxHeight: '360px', overflowY: 'auto', padding: paperPadding, backgroundColor: '#f7f0f1', boxSizing: 'border-box' }}>
        {selectedRequirement && (
          <Box style={{ marginBottom: theme.spacing(2) }}>
            <Typography style={{ fontWeight: 600, marginBottom: theme.spacing(1) }}>
              {formatMessage({ id: 'law-portal.requirements.compliance.table.requirement' })}
            </Typography>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', backgroundColor: '#fff', borderRadius: 4 }}>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, backgroundColor: '#e3f2fd', borderRadius: 6 }}>
                <DescriptionOutlinedIcon style={{ color: '#1976d2', fontSize: 24 }} />
              </Box>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {selectedRequirement.number && `${selectedRequirement.number} – `}{selectedRequirement.name}
              </Typography>
            </Box>
          </Box>
        )}

        {selectedCompanyUnits.length > 0 && (
          <Box style={{ marginBottom: theme.spacing(2) }}>
            <Typography style={{ fontWeight: 600, marginBottom: theme.spacing(1) }}>
              {formatMessage({ id: 'form.selectCompanyUnits' })} ({selectedCompanyUnits.length})
            </Typography>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {selectedCompanyUnits.map((unit) => (
                <Box key={String(unit.value)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', backgroundColor: '#fff', borderRadius: 4 }}>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, backgroundColor: GREEN, borderRadius: 6 }}>
                    <BusinessOutlinedIcon style={{ color: '#fff', fontSize: 24 }} />
                  </Box>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>{unit.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {selectedManagers.length > 0 && (
          <Box>
            <Typography style={{ fontWeight: 600, marginBottom: theme.spacing(1) }}>
              {formatMessage({ id: 'law-portal.manage.edit-law.complianceUsers' })} ({selectedManagers.length})
            </Typography>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {selectedManagers.map((manager) => (
                <Box key={String(manager.value)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', backgroundColor: '#fff', borderRadius: 4 }}>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, backgroundColor: GREEN, borderRadius: 6 }}>
                    <PersonOutlineIcon style={{ color: '#fff', fontSize: 24 }} />
                  </Box>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>{manager.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </div>

      <Box style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
        <Button variant="outlined" onClick={onSaveAndNewConnection} disabled={isSaving} style={{ ...outlinedRedBtn, minWidth: 180 }}>
          {formatMessage({ id: 'form.save-and-new-connection' })}
        </Button>
        <Button variant="contained" onClick={onSaveAndQuit} disabled={isSaving} style={{ backgroundColor: PRIMARY, color: '#fff', minWidth: 160 }}>
          {isSaving ? <CircularProgress size={20} /> : formatMessage({ id: 'form.save-and-quit' })}
        </Button>
      </Box>
    </div>
  );
};

export default Step5RequirementNewCompanyUnitPreview;
